import { styled } from '@mui/material/styles'
import { Box, Paper, Card, Chip, Button } from '@mui/material'

export const OverallRosterContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  backgroundColor: theme.palette.mode === 'dark' 
    ? 'rgba(18, 18, 18, 0.95)' 
    : 'rgba(250, 250, 252, 0.98)',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: theme.palette.mode === 'dark'
      ? 'linear-gradient(135deg, rgba(25, 118, 210, 0.05) 0%, rgba(156, 39, 176, 0.05) 100%)'
      : 'linear-gradient(135deg, rgba(25, 118, 210, 0.02) 0%, rgba(156, 39, 176, 0.02) 100%)',
    pointerEvents: 'none',
    zIndex: 0,
  },
  '& > *': {
    position: 'relative',
    zIndex: 1,
  },
}))

export const ViewModeContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.mode === 'dark'
    ? 'rgba(30, 30, 30, 0.8)'
    : 'rgba(255, 255, 255, 0.7)',
  backdropFilter: 'blur(12px) saturate(180%)',
  WebkitBackdropFilter: 'blur(12px) saturate(180%)',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
}))

export const FiltersContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  margin: theme.spacing(2),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: theme.shadows[2],
  backgroundColor: theme.palette.mode === 'dark'
    ? 'rgba(30, 30, 30, 0.75)'
    : 'rgba(255, 255, 255, 0.75)',
  backdropFilter: 'blur(10px) saturate(180%)',
  WebkitBackdropFilter: 'blur(10px) saturate(180%)',
  border: '1px solid',
  borderColor: theme.palette.mode === 'dark'
    ? 'rgba(255, 255, 255, 0.1)'
    : 'rgba(0, 0, 0, 0.08)',
}))

export const SummaryCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: theme.shadows[3],
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[6],
  },
}))

export const RosterSlotCard = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'isUnoccupied' && prop !== 'color',
})(({ theme, color, isUnoccupied }) => {
  // Soothing green/teal color for set slots (default)
  const defaultSlotColor = '#26A69A' // Soothing teal-green (Material Design Teal 400)
  const slotColor = color || defaultSlotColor
  
  return {
    padding: theme.spacing(1.25),
    marginBottom: theme.spacing(0.5),
    borderRadius: theme.shape.borderRadius * 1.5,
    backgroundColor: isUnoccupied
      ? 'rgba(245, 245, 245, 0.6)'
      : slotColor,
    color: isUnoccupied ? theme.palette.text.secondary : theme.palette.getContrastText(slotColor),
    cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    border: isUnoccupied 
      ? `2px dashed ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : theme.palette.grey[300]}` 
      : 'none',
    boxShadow: isUnoccupied 
      ? 'none' 
      : `0 2px 8px ${slotColor ? `${slotColor}40` : 'rgba(38, 166, 154, 0.25)'}`,
    fontWeight: 500,
    fontSize: '0.875rem',
    position: 'relative',
    overflow: 'hidden',
    // Glass effect for occupied slots
    ...(!isUnoccupied && {
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '3px',
        background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
      },
    }),
    '&:hover': {
      transform: 'translateY(-2px) scale(1.01)',
      boxShadow: isUnoccupied 
        ? `0 4px 12px rgba(0, 0, 0, 0.1)` 
        : `0 4px 16px ${slotColor ? `${slotColor}60` : 'rgba(38, 166, 154, 0.35)'}`,
      borderColor: isUnoccupied ? theme.palette.primary.main : undefined,
    },
  }
})

export const WeekDayHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.75),
  textAlign: 'center',
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: theme.palette.primary.contrastText,
  fontWeight: 600,
  fontSize: '0.875rem',
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '2px',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  },
}))

export const RoomRow = styled(Box)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child': {
    borderBottom: 'none',
  },
}))

export const ModernButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 2,
  textTransform: 'none',
  fontWeight: 600,
  padding: theme.spacing(1, 2.5),
  boxShadow: theme.shadows[2],
  '&:hover': {
    boxShadow: theme.shadows[4],
  },
}))

export const StatValue = styled(Box)(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 700,
  color: theme.palette.primary.main,
  lineHeight: 1.2,
}))

export const StatLabel = styled(Box)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
  marginTop: theme.spacing(0.5),
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
}))
